import React, { useMemo } from "react";
import dayjs from "dayjs";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    ReferenceLine,
    DefaultLegendContent,
} from "recharts";

export type PromotionCostBreakdownYoYRow = {
    month: string; // "YYYY/MM"

    // 前期（前年同月）
    point_prev: number;
    mail_prev: number;
    affiliate_prev: number;
    ads_prev: number;

    // 今期
    point_cur: number;
    mail_cur: number;
    affiliate_cur: number;
    ads_cur: number;
};

type Props = {
    data: PromotionCostBreakdownYoYRow[];
    loading?: boolean;
    emptyLabel?: string;
    title?: string;

    // ✅ OFF: 今期のみ / ON: 前期+今期
    showCompare?: boolean;
    onChangeShowCompare?: (v: boolean) => void;
    compareLabel?: string;

    // ✅ 日次利益で使うための差し込み口（未指定ならダッシュボードの見た目のまま）
    formatTick?: (m: string) => string; // X軸の文字（既定: month をそのまま）
    formatTooltipTitle?: (m: string, p: string) => string; // 吹き出しの見出し（既定: "m（p）"）
    autoBarSize?: boolean; // true: 棒の太さを幅に合わせる（既定: 28px 固定）
    frameless?: boolean; // true: 外枠なし（開閉枠の中に入れるとき）
};

type ChartRow = {
    x: string; // unique key: "YYYY/MM|前期" etc
    point: number;
    mail: number;
    affiliate: number;
    ads: number;
};

const LEGEND_ORDER = ["point", "mail", "affiliate", "ads"];

function fmtCompactYen(v: number) {
    const n = Number(v || 0);
    const a = Math.abs(n);
    if (a >= 1e8) return `${(n / 1e8).toFixed(1)}億`;
    if (a >= 1e4) return `${(n / 1e4).toFixed(1)}万`;
    return `${Math.round(n).toLocaleString("ja-JP")}`;
}

function fmtYen(v: number) {
    return `${Math.round(Number(v || 0)).toLocaleString("ja-JP")}円`;
}

function sumRow(r: any) {
    return (
        (Number(r?.point ?? 0) || 0) +
        (Number(r?.mail ?? 0) || 0) +
        (Number(r?.affiliate ?? 0) || 0) +
        (Number(r?.ads ?? 0) || 0)
    );
}

export default function PromotionCostBreakdownYoYChart({
    data,
    loading = false,
    emptyLabel = "データがありません",
    title = "販促費内訳（ポイント・メルマガ・アフィリ・広告費）",
    showCompare = false,
    onChangeShowCompare,
    compareLabel = "前期比較（前年同月）",
    formatTick,
    formatTooltipTitle,
    autoBarSize = false,
    frameless = false,
}: Props) {
    const barSize = autoBarSize ? undefined : 28;
    const hasData = Array.isArray(data) && data.length > 0;


    const BASE = {
        blue: "#5C97E3",
        yellow: "#FED561",
        orange: "#FEB072",
        red: "#FB7772",
        cyan: "#5DBEE9",
    };
    const MIX = {
        blueCyan: "#5CAAE6", // blue + cyan
        yellowOrange: "#FEC26A", // yellow + orange
        orangeRed: "#FC9472", // orange + red
    };
    const ADD = {
        deepBlue: "#2F6FE6", // ベース青を締めるロイヤルブルー
        deepCyan: "#00A6D6", // シアンを締めるオーシャン系
        indigo: "#6F5CF0", // 青系の延長にあるインディゴ
    };
    const DARK = {
        blue: "#4571AA",
        red: "#BC5956",
        cyan: "#468EAF",
        // 必要ならさらに：
        orange: "#BE8456",
        yellow: "#BEA049",
    };

    const COLORS_PROMOTION = {
        point: BASE.cyan,
        mail: BASE.red,
        affiliate: BASE.orange,
        ads: BASE.yellow,
    };



    // ✅ ここが肝：month+prev/cur を「前期行」「今期行」に展開
    const chartData: ChartRow[] = useMemo(() => {
        const out: ChartRow[] = [];
        for (const r of data || []) {
            const m = r.month;

            // "YYYY/MM" → dayjsで扱える "YYYY-MM-01" に寄せる
            const prevM = (() => {
                const ym = String(m).replace(/\//g, "-"); // "YYYY-MM"
                const d = dayjs(ym + "-01");
                return d.isValid() ? d.subtract(1, "year").format("YYYY/MM") : String(m);
            })();

            if (showCompare) {
                out.push({
                    x: `${prevM}|前期`, // ✅ 前期は前年同月の年月を表示
                    point: Number(r.point_prev ?? 0) || 0,
                    mail: Number(r.mail_prev ?? 0) || 0,
                    affiliate: Number(r.affiliate_prev ?? 0) || 0,
                    ads: Number(r.ads_prev ?? 0) || 0,
                });
            }

            out.push({
                x: `${m}|今期`, // ✅ 今期はそのまま
                point: Number(r.point_cur ?? 0) || 0,
                mail: Number(r.mail_cur ?? 0) || 0,
                affiliate: Number(r.affiliate_cur ?? 0) || 0,
                ads: Number(r.ads_cur ?? 0) || 0,
            });

        }
        return out;
    }, [data, showCompare]);

    // ✅ X軸：月は「前期」のときだけ表示（2本並びが見やすい）
    const MultiLineTick = (props: any) => {
        const { x, y, payload } = props;
        const raw = String(payload?.value ?? "");
        const [m, p] = raw.split("|"); // "YYYY/MM", "前期/今期"

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} textAnchor="end" fill="#666" fontSize={11} transform="rotate(-45)">
                    {formatTick ? formatTick(m) : m}
                </text>
            </g>
        );
    };



    return (
        <div className={frameless ? "pt-2" : "rounded-2xl border bg-white p-4"}>
            {/* タイトル：提示コードと同じ */}
            <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-xs font-semibold text-slate-800">{title}</div>

                {onChangeShowCompare && (
                    <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
                        <input
                            type="checkbox"
                            checked={showCompare}
                            onChange={(e) => onChangeShowCompare(e.target.checked)}
                        />
                        {compareLabel}
                    </label>
                )}
            </div>

            {!hasData ? (
                <div className="text-sm text-slate-500">
                    {loading ? "読み込み中..." : emptyLabel}
                </div>
            ) : (
                <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                        {/* ✅ keyで強制再マウント（Rechartsの差分反映が鈍いケース対策） */}
                        <BarChart
                            key={showCompare ? "cmp-on" : "cmp-off"}
                            data={chartData}
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                            barCategoryGap={14}
                            barGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis
                                dataKey="x"
                                tickMargin={12}
                                tick={<MultiLineTick />}
                                height={64}
                                interval={0}
                                minTickGap={16}
                                padding={{ left: 30, right: 40 }}
                            />


                            <YAxis
                                tickFormatter={(v) => fmtCompactYen(Number(v))}
                                width={56}
                                tick={{ fontSize: 11 }}
                            />

                            <ReferenceLine yAxisId="right" y={0} strokeDasharray="4 4" />

                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload || !payload.length) return null;

                                    const row: any = payload?.[0]?.payload ?? {};
                                    const raw = String(label ?? "");
                                    const [m, p] = raw.split("|");

                                    const total = sumRow(row);

                                    return (
                                        <div className="rounded-lg border bg-white px-3 py-2 text-xs shadow">
                                            <div className="mb-1 font-semibold">
                                                {formatTooltipTitle ? formatTooltipTitle(m, p) : `${m}（${p}）`}
                                            </div>
                                            <div className="space-y-1 text-slate-700">
                                                <div className="font-semibold">合計: {fmtYen(total)}</div>
                                                <div>ポイント: {fmtYen(row.point)}</div>
                                                <div>メルマガ: {fmtYen(row.mail)}</div>
                                                <div>アフィリ: {fmtYen(row.affiliate)}</div>
                                                <div>広告費: {fmtYen(row.ads)}</div>
                                            </div>
                                        </div>
                                    );
                                }}
                            />

                            {/* ✅ 積み上げ（常に stackId="a"） */}
                            <Bar dataKey="point" stackId="a" name="ポイント" fill={COLORS_PROMOTION.point} barSize={barSize} isAnimationActive={false} />
                            <Bar dataKey="mail" stackId="a" name="メルマガ" fill={COLORS_PROMOTION.mail} barSize={barSize} isAnimationActive={false} />
                            <Bar dataKey="affiliate" stackId="a" name="アフィリ" fill={COLORS_PROMOTION.affiliate} barSize={barSize} isAnimationActive={false} />
                            <Bar dataKey="ads" stackId="a" name="広告費" fill={COLORS_PROMOTION.ads} barSize={barSize} isAnimationActive={false} />

                            <Legend
                                wrapperStyle={{ fontSize: 11 }}
                                content={(props: any) => {
                                    const payload = props?.payload || [];
                                    const sorted = [...payload].sort((a: any, b: any) => {
                                        const ai = LEGEND_ORDER.indexOf(a.dataKey);
                                        const bi = LEGEND_ORDER.indexOf(b.dataKey);
                                        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                                    });
                                    return <DefaultLegendContent {...props} payload={sorted} />;
                                }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
